import type * as Sentry from '@sentry/react'
import { type StoreUserType, getLocalStorageSessionValue } from '../hooks/use_session_store'

/**
 * Identifies and filters every event on its way to Sentry.
 *
 * Identity is read from localStorage on each event rather than pushed via `Sentry.setUser` at
 * login, so it survives a page reload and cannot go stale after logout. Before this, every event
 * arrived as `user: ip:…` and an auth capture could not be attributed to an account — diagnosing
 * a 403 meant guessing which user had hit it.
 */
export const sentryBeforeSend = (event: Sentry.ErrorEvent, _hint?: Sentry.EventHint): Sentry.ErrorEvent | null => {
  const user = getLocalStorageSessionValue('user') as Partial<StoreUserType & { email: string }>

  if (user?.email) {
    event.user = { ...event.user, email: user.email, ...(user.pgId ? { id: String(user.pgId) } : {}) }
  }

  // The API rejects readonly users on every POST/PUT/PATCH/DELETE that lacks @AllowReadonly,
  // which is a frequent — and otherwise invisible — cause of a 403.
  if (typeof user?.readonly === 'boolean') {
    event.tags = { ...event.tags, user_readonly: user.readonly }
  }

  // Auth captures (401/403 from the API layer) should always reach Sentry, even for
  // internal users, so we can monitor auth failures across the board.
  if (event.tags?.auth_capture === 'true') {
    return event
  }
  // Filtering out ecoplanet.tech users as these are internal users and sometimes,
  // they are not set properly so there is greater likelihood of errors
  // Linear Ticket - https://linear.app/ecoplanet/issue/EC-3515/seamless-session-sentry-cleanup
  if (user?.email?.endsWith('@ecoplanet.tech')) {
    return null
  }
  return event
}
